// Реальный WebSocket-транспорт протокола поддержки.
// URL сокета — из env REACT_APP_SUPPORT_WS (CRA подставляет REACT_APP_* в process.env).
//
// Контракт (совпадает с mockSocket):
//   connect()                  -> Promise — подключение + auth-handshake
//   request(endpoint, payload) -> Promise<payload> (reject: { kind, details } | Error)
//   on(eventName, fn)          -> unsubscribe
//
// Особенности протокола:
//   - первый запрос после подключения — auth с токеном из localStorage (или null,
//     сервер выдаёт новый токен — сохраняем, чтобы история переживала перезагрузку);
//   - ответы сопоставляются по request_id; сообщения без request_id — события;
//   - при разрыве все pending-запросы реджектятся, соединение восстанавливается
//     с экспоненциальным backoff (1s -> 30s) и обязательным повторным auth.

import { TOKEN_KEY } from "./mockSocket";

const getWsUrl = () => process.env.REACT_APP_SUPPORT_WS;

const CONNECT_TIMEOUT = Number(process.env.REACT_APP_SUPPORT_CONNECT_TIMEOUT_MS) || 10_000;
const REQUEST_TIMEOUT = 15_000;
const MAX_RECONNECT_DELAY = 30_000;

let ws = null;
let connectPromise = null;
let reconnectAttempt = 0;
let reconnectTimer = null;

let requestId = 0;
const pending = new Map(); // request_id -> { resolve, reject, timer }
const outbox = []; // сообщения, отправленные до открытия сокета
const listeners = new Map(); // event_name -> Set<fn>

const getToken = () =>
	typeof localStorage !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;

const clearToken = () => {
	if (typeof localStorage !== "undefined") localStorage.removeItem(TOKEN_KEY);
};

// Первый запрос после подключения — auth. Если сервер не признал сохранённый
// токен (устарел после пересоздания бэкенда, либо в localStorage остался
// моковый UUID от mockSocket) — сбрасываем его и регистрируемся заново
// с token: null; сервер выдаст новый токен.
function authRequest() {
	return request("auth", { token: getToken() }).catch((error) => {
		if (getToken() == null) throw error;
		clearToken();
		return request("auth", { token: null });
	});
}

function emit(eventName, payload) {
	listeners.get(eventName)?.forEach((fn) => fn(payload));
}

function rejectAllPending(error) {
	pending.forEach(({ reject, timer }) => {
		clearTimeout(timer);
		reject(error);
	});
	pending.clear();
}

function sendMessage(message) {
	if (ws && ws.readyState === WebSocket.OPEN) {
		ws.send(JSON.stringify(message));
	} else {
		outbox.push(message);
	}
}

export function request(endpoint, payload) {
	return new Promise((resolve, reject) => {
		const id = ++requestId;
		const entry = {
			resolve: (value) => {
				clearTimeout(entry.timer);
				resolve(value);
			},
			reject: (error) => {
				clearTimeout(entry.timer);
				reject(error);
			},
			timer: setTimeout(() => {
				if (pending.delete(id)) reject(new Error(`timeout: ${endpoint}`));
			}, REQUEST_TIMEOUT),
		};
		pending.set(id, entry);
		sendMessage({ request_id: id, endpoint, payload });
	});
}

function scheduleReconnect() {
	if (reconnectTimer || typeof window === "undefined") return;
	const delay = Math.min(MAX_RECONNECT_DELAY, 1000 * 2 ** reconnectAttempt++);
	reconnectTimer = setTimeout(() => {
		reconnectTimer = null;
		// eslint-disable-next-line no-use-before-define
		connect().catch(() => {
			/* разорвалось снова — onclose перепланирует reconnect */
		});
	}, delay);
}

function openSocket() {
	return new Promise((resolve, reject) => {
		const socket = new WebSocket(getWsUrl());
		ws = socket;
		let settled = false;
		let connectTimer = null;

		const fail = (error) => {
			if (settled) return;
			settled = true;
			clearTimeout(connectTimer);
			reject(error);
		};

		socket.onmessage = (event) => {
			let data;
			try {
				data = JSON.parse(event.data);
			} catch {
				return;
			}

			// Ответ на запрос
			if (data.request_id !== undefined && pending.has(data.request_id)) {
				const entry = pending.get(data.request_id);
				pending.delete(data.request_id);
				if (data.success) entry.resolve(data.payload);
				else entry.reject({ kind: data.kind, details: data.details });
				return;
			}

			// Событие с сервера
			if (data.event_name) emit(data.event_name, data.payload);
		};

		socket.onopen = () => {
			// Первый запрос после подключения — auth. request() поставит его
			// в outbox первым, затем отправим всё накопленное.
			const auth = authRequest();
			while (outbox.length) socket.send(outbox.shift());

			auth
				.then((payload) => {
					if (payload?.token && payload.token !== getToken()) {
						localStorage.setItem(TOKEN_KEY, payload.token);
					}
					reconnectAttempt = 0;
					settled = true;
					clearTimeout(connectTimer);
					resolve();
				})
				.catch(fail);
		};

		socket.onclose = () => {
			if (ws === socket) ws = null;
			outbox.length = 0;
			rejectAllPending(new Error("connection closed"));
			fail(new Error("connection closed before auth"));
			scheduleReconnect();
		};

		socket.onerror = () => {
			// браузер вслед за onerror всегда вызывает onclose — обработка там
		};

		// Если auth-ответ потерян (полумёртое соединение) — не ждём вечно:
		// закрываем сокет, onclose запустит цикл reconnect
		connectTimer = setTimeout(() => {
			fail(new Error("connect timeout"));
			socket.close();
		}, CONNECT_TIMEOUT);
	});
}

export function connect() {
	if (typeof WebSocket === "undefined") {
		return Promise.reject(new Error("WebSocket is not available"));
	}
	if (ws && ws.readyState === WebSocket.OPEN) return Promise.resolve();
	if (connectPromise) return connectPromise;
	connectPromise = openSocket().finally(() => {
		connectPromise = null;
	});
	return connectPromise;
}

export function on(eventName, fn) {
	if (!listeners.has(eventName)) listeners.set(eventName, new Set());
	listeners.get(eventName).add(fn);
	return () => listeners.get(eventName)?.delete(fn);
}
